// =============================================================================
// CONTENT STORAGE SERVICE
// =============================================================================
// Service for managing content storage in Firebase Storage

import { ref, uploadString, getDownloadURL, listAll, deleteObject, getMetadata } from 'firebase/storage';
import { storage, isFirebaseConfigured } from './firestore';
import type { PageData } from '@/utils/serialization';
import { toJSON, fromJSON, validatePageData } from '@/utils/serialization';

/**
 * Storage path structure: clients/{clientId}/pages/{pageId}.json
 */

export interface StorageMetadata {
  pageId: string;
  version: string;
  lastModified: string;
  size: number;
}

export class ContentStorageService {
  private clientId: string;

  constructor(clientId: string = import.meta.env.VITE_CLIENT_ID || 'awani') {
    this.clientId = clientId;
  }

  /**
   * Get the storage path for a published page
   * Matches admin console publishing path: content/published/{pageId}.json
   */
  private getPagePath(pageId: string): string {
    return `content/published/${pageId}.json`;
  }

  /**
   * Save page data to Firebase Storage
   */
  async savePage(pageData: PageData): Promise<boolean> {
    if (!isFirebaseConfigured || !storage) return false;
    
    try {
      const storageRef = ref(storage, this.getPagePath(pageData.pageId));
      const json = toJSON({
        ...pageData,
        lastModified: new Date().toISOString(),
      });
      
      await uploadString(storageRef, json, 'raw', {
        contentType: 'application/json',
        customMetadata: {
          pageId: pageData.pageId,
          version: pageData.version,
        },
      });
      
      return true;
    } catch (error) {
      // Error saving page
      return false;
    }
  }

  /**
   * Load published page data from Firebase Storage
   * Path: content/published/{pageId}.json
   */
  async loadPage(pageId: string): Promise<PageData | null> {
    if (!isFirebaseConfigured || !storage) {
      return null;
    }
    
    try {
      const path = this.getPagePath(pageId);
      const storageRef = ref(storage, path);
      
      // Get download URL
      const url = await getDownloadURL(storageRef);
      
      // Fetch the JSON
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const json = await response.text();
      
      // Validate and parse
      const parsed = JSON.parse(json);
      if (!validatePageData(parsed)) {
        throw new Error('Invalid page data format');
      }
      
      const pageData = fromJSON(json);
      return pageData;
    } catch (error) {
      // Check if it's a "not found" error (404) - file doesn't exist yet
      if (error instanceof Error && (
        error.message.includes('404') ||
        error.message.includes('object-not-found') ||
        error.message.includes('storage/object-not-found')
      )) {
        return null;
      }
      
      // Check if it's a connection error
      if (error instanceof Error && (
        error.message.includes('ERR_CONNECTION_REFUSED') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('NetworkError')
      )) {
        return null;
      }
      
      // Error loading page from Firebase Storage
      return null;
    }
  }

  /**
   * Check if a page exists in storage
   */
  async pageExists(pageId: string): Promise<boolean> {
    if (!isFirebaseConfigured || !storage) return false;
    
    try {
      const storageRef = ref(storage, this.getPagePath(pageId));
      await getMetadata(storageRef);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Delete a page from storage
   */
  async deletePage(pageId: string): Promise<boolean> {
    if (!isFirebaseConfigured || !storage) return false;
    
    try {
      const storageRef = ref(storage, this.getPagePath(pageId));
      await deleteObject(storageRef);
      return true;
    } catch (error) {
      // Error deleting page
      return false;
    }
  }

  /**
   * List all published pages
   */
  async listPages(): Promise<StorageMetadata[]> {
    if (!isFirebaseConfigured || !storage) return [];
    
    try {
      const pagesRef = ref(storage, 'content/published');
      const result = await listAll(pagesRef);
      const pages: StorageMetadata[] = [];
      
      for (const itemRef of result.items) {
        try {
          const metadata = await getMetadata(itemRef);
          const customMeta = metadata.customMetadata || {};
          
          pages.push({
            pageId: customMeta.pageId || itemRef.name.replace('.json', ''),
            version: customMeta.version || '1.0.0',
            lastModified: metadata.updated,
            size: metadata.size,
          });
        } catch {
          // Skip items we can't read
        }
      }
      
      return pages;
    } catch (error) {
      // Error listing pages
      return [];
    }
  }

  /**
   * Copy a page to a new ID
   */
  async copyPage(sourcePageId: string, targetPageId: string): Promise<boolean> {
    try {
      const pageData = await this.loadPage(sourcePageId);
      if (!pageData) {
        throw new Error(`Source page not found: ${sourcePageId}`);
      }
      
      // Update the page ID
      const newPageData: PageData = {
        ...pageData,
        pageId: targetPageId,
      };
      
      return await this.savePage(newPageData);
    } catch (error) {
      // Error copying page
      return false;
    }
  }

  /**
   * Get page metadata without downloading full content
   */
  async getPageMetadata(pageId: string): Promise<StorageMetadata | null> {
    if (!isFirebaseConfigured || !storage) return null;
    
    try {
      const storageRef = ref(storage, this.getPagePath(pageId));
      const metadata = await getMetadata(storageRef);
      const customMeta = metadata.customMetadata || {};
      
      return {
        pageId: customMeta.pageId || pageId,
        version: customMeta.version || '1.0.0',
        lastModified: metadata.updated,
        size: metadata.size,
      };
    } catch (error) {
      // Error getting metadata
      return null;
    }
  }

  /**
   * Change the client context
   */
  setClientId(clientId: string): void {
    this.clientId = clientId;
  }

  /**
   * Get the current client ID
   */
  getClientId(): string {
    return this.clientId;
  }
}

// Export a singleton instance with client ID from environment or default
export const contentStorage = new ContentStorageService(import.meta.env.VITE_CLIENT_ID || 'awani');

// Export the class for creating client-specific instances
export default ContentStorageService;

